import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { AutoCarousel } from "@components/media/AutoCarousel"
import { Chip } from "@components/ui/Chip"
import { brandVariants } from "@utils/brand-variants"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, useInlineEditableValue } from "eddev/blocks"
import { Link } from "eddev/routing"
import { AnimatePresence, motion, MotionProps } from "motion/react"

export const meta: BlockMeta = {
  title: "CTA Card",
  category: "layouts",
  icon: "admin-links",
  description: "A card representing a CTA",
  keywords: ["category", "genre", "tile"],
}

const fadeVariants: MotionProps = {
  variants: {
    hide: { opacity: 0 },
    show: { opacity: 1 },
  },
}

export default defineBlock("content/cta-cards-item", (props) => {
  const [title] = useInlineEditableValue<string>("title")
  const images = props.images || []
  const filmImages = props.films?.map((film) => film?.film?.media?.thumb).filter(Boolean) || []
  const media = props.mediaType === "images" ? images : filmImages

  return (
    <div className="flex flex-col gap-3" data-mixpanel-heading={title}>
      {media.length > 0 && (
        <div className="w-full aspect-video relative pointer-events-none">
          {media.length === 1 ? (
            <ResponsiveImage {...media[0]} objectFit="cover" className="absolute w-full h-full" />
          ) : (
            <AutoCarousel
              aspect={16 / 9}
              speed={0.1}
              items={media.map(
                (img, ix) =>
                  img && (
                    <div key={ix} className="flex pr-3 h-full">
                      <ResponsiveImage srcSet={img.srcSet} objectFit="cover" className="w-full h-full" />
                    </div>
                  ),
              )}
            />
          )}
        </div>
      )}

      <Link
        href={props.link?.url}
        target={props.link?.target}
        draggable={false}
        data-mixpanel-block="CTA Card Item"
        className={cn("flex type-title-l h-[8em] p-5 duration-400 md:hover:rounded-md transition-all card-item")}
        title={title!}
      >
        <AnimatePresence mode="wait">
          <motion.div
            initial="hide"
            animate="show"
            exit="hide"
            className="flex flex-col h-full justify-between"
            transition={{ staggerChildren: 0.05 }}
          >
            <motion.div className="type-title-l" {...fadeVariants}>
              <EditableText store="title" as="div" defaultValue="Enter a title" plainText className="text-pretty" />
            </motion.div>

            <div className="flex flex-col w-full">
              <motion.div {...fadeVariants}>
                <Chip className="w-fit">
                  <EditableText store="chip" as="span" defaultValue="Enter a label" plainText />
                </Chip>
              </motion.div>
              <motion.div className="flex flex-col gap-3 type-body-l [&_p:last-child]:mb-0" {...fadeVariants}>
                <EditableText
                  store="description"
                  as="div"
                  placeholder="Enter a description"
                  plainText
                  className="text-balance type-body-m mt-2"
                />
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      </Link>
    </div>
  )
})
